"use client";

import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldGroup } from "@/components/ui/field";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { signInWithOAuth } from "@/lib/auth/auth";
import { SITE_NAME } from "@/lib/constants";
import { EmailSchema, cn } from "@/lib/utils";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { ArrowRightIcon, MailIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FaApple } from "react-icons/fa";
import * as v from "valibot";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../ui/card";
import GoogleIcon from "../ui/icons/GoogleIcon";
import ZeekMapsIcon from "../ui/icons/ZeekMapsIcon";
import SignUpEmail from "./sign-up-email";

interface SignUpFormParams {
	className?: string;
}

const schema = v.object({
	email: EmailSchema,
});

export default function SignUp({ className }: SignUpFormParams) {
	const [isLoading, setIsLoading] = useState(false);
	const [email, setEmail] = useState<string | null>(null);

	const handleGoogleSignUp = async (e: React.FormEvent) => {
		setIsLoading(true);
		try {
			const { success, error } = await signInWithOAuth({ provider: "google" });
			if (error) throw ErrorEvent;
		} catch (error: unknown) {
			console.error("handleGoogleSignUp error: ", error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleAppleSignUp = async (e: React.FormEvent) => {
		setIsLoading(true);
		try {
			const { success, error } = await signInWithOAuth({ provider: "apple" });
			if (error) throw ErrorEvent;
		} catch (error: unknown) {
			console.error("handleAppleSignUp error: ", error);
		} finally {
			setIsLoading(false);
		}
	};

	const form = useForm({
		resolver: valibotResolver(schema),
		defaultValues: {
			email: "",
		},
	});

	async function onSubmit(data: v.InferInput<typeof schema>) {
		setIsLoading(true);
		setEmail(data.email);
	}

	if (email) {
		return <SignUpEmail email={email} />;
	}

	return (
		<Card className={cn("bg-transparent border-none shadow-none", className)}>
			<CardHeader className="items-center justify-center text-center space-y-4">
				<ZeekMapsIcon className="w-16 h-16" />
				<CardTitle className="text-3xl font-extrabold tracking-tighter">
					Sign up for {SITE_NAME}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="flex flex-col w-full items-center justify-center place-items-center gap-4">
					{/* Google Sign Up */}
					<Button
						type="button"
						onClick={handleGoogleSignUp}
						variant="default"
						className="w-full rounded-full"
					>
						<GoogleIcon className="mr-2 w-4 h-4" /> Sign up with Google
					</Button>

					{/* Apple Sign Up */}
					<Button
						type="button"
						onClick={handleAppleSignUp}
						variant="default"
						className="w-full rounded-full"
					>
						<FaApple className="mr-2 w-5 h-5 fill-background" /> Sign up with
						Apple
					</Button>

					<div className="flex w-full items-center">
						<hr className="flex-grow border-0.1 border-primary/20" />
						<span className="text-md font-semibold text-primary mx-2">or</span>
						<hr className="flex-grow border-0.1 border-primary/20" />
					</div>

					{/* Email Sign Up */}
					<form
						id="form-sign-up"
						onSubmit={form.handleSubmit(onSubmit)}
						className="w-full"
					>
						<FieldGroup>
							{/* Email */}
							<Controller
								name="email"
								control={form.control}
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid}>
										<InputGroup className="rounded-full">
											<InputGroupInput
												id="form-sign-up-email"
												type="text"
												autoComplete="email"
												placeholder="Enter your email"
												aria-invalid={fieldState.invalid}
												{...field}
											/>
											<InputGroupAddon className="gap-2">
												<MailIcon />
											</InputGroupAddon>
										</InputGroup>

										{fieldState.invalid && (
											<FieldError errors={[fieldState.error]} />
										)}
									</Field>
								)}
							/>
						</FieldGroup>
					</form>
				</div>
			</CardContent>

			<CardFooter>
				<div className="w-full flex flex-col space-y-2">
					<div className="w-full flex gap-2 items-center justify-end">
						<Button
							size="default"
							type="submit"
							className="w-full rounded-full"
							form="form-sign-up"
							disabled={isLoading}
						>
							Next
							<ArrowRightIcon className="w-3 h-3" />
						</Button>
					</div>
					<p className="text-[0.6rem] text-center text-primary-64">
						By signing up, you agree to the{" "}
						<Link
							href="/terms"
							className="text-link hover:underline hover:underline-offset-4 visited:text-link-visited"
						>
							Terms of Service
						</Link>{" "}
						and{" "}
						<Link
							href="/privacy"
							className="text-link hover:underline hover:underline-offset-4 visited:text-link-visited"
						>
							Privacy Policy
						</Link>
						.
					</p>
					<div className="text-center text-sm">
						Already have an account?{" "}
						<Link
							href="/auth/sign-in"
							className="font-semibold text-link hover:underline hover:underline-offset-4 visited:text-link-visited"
						>
							Sign In
						</Link>
					</div>
				</div>
			</CardFooter>
		</Card>
	);
}
